import React from 'react';
import { Loader2, TrendingUp, Pencil, Plus, Trash2, Check, X } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../components/ui/command';

// ── SiteExclusionPicker ──────────────────────────────────────────
// Compact multi-select checkbox list for choosing which sites within a
// client group should be EXCLUDED from a given uplift.
//
// Props:
//   sites    — [{id, name}] — only the sites belonging to the relevant client group
//   selected — Set<number>  — site IDs currently excluded
//   onChange — (Set<number>) => void
//   disabled — bool
function SiteExclusionPicker({ sites, selected, onChange, disabled = false }) {
  const [open, setOpen] = React.useState(false);
  const count = selected instanceof Set ? selected.size : 0;
  const isExcluded = (id) => selected instanceof Set && selected.has(id);

  const toggle = (id) => {
    const next = new Set(selected instanceof Set ? selected : []);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(next);
  };

  if (!sites.length) return (
    <span className="text-xs text-slate-400 italic">No sites in group yet</span>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ' +
            (count > 0 ? 'border-amber-400 bg-amber-50' : '')
          }
          data-testid="uplift-excl-trigger"
        >
          <span className={count > 0 ? 'text-amber-700 font-medium' : 'text-slate-400'}>
            {count === 0
              ? 'None excluded — all sites in group get uplift'
              : `${count} site${count === 1 ? '' : 's'} excluded from uplift`}
          </span>
          <span className="ml-2 text-slate-400">▾</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid="uplift-excl-popover">
        <div className="px-3 py-2 border-b text-xs font-semibold text-slate-600 bg-slate-50">
          Check sites to <strong>exclude</strong> from this uplift
        </div>
        <div className="max-h-52 overflow-y-auto">
          {sites.map(s => (
            <label
              key={s.id}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer select-none"
              data-testid={`excl-site-option-${s.id}`}
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded accent-amber-600"
                checked={isExcluded(s.id)}
                onChange={() => toggle(s.id)}
                data-testid={`excl-site-checkbox-${s.id}`}
              />
              <span className="text-sm">{s.name}</span>
            </label>
          ))}
        </div>
        {count > 0 && (
          <div className="border-t px-3 py-1.5 flex justify-between items-center bg-slate-50">
            <span className="text-xs text-amber-700">{count} excluded</span>
            <button
              type="button"
              onClick={() => onChange(new Set())}
              className="text-xs text-rose-600 hover:underline"
              data-testid="excl-clear-all"
            >
              Clear all
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


// ── UpliftDialog (Budget UAT — Group-only site exclusions) ────────
// Manage % uplifts scoped to Global or Client (group). Client-scope
// uplifts optionally exclude specific sites in that group from receiving
// the uplift. `excluded_site_ids` is validated server-side against the
// org and the uplift's client group; foreign or deleted site IDs are
// silently dropped.
export function UpliftDialog({
  uplifts, clients, sites = [], dateRange,
  siteCountByClient = {}, totalSiteCount = 0,
  clientsWithClientUpliftIds = new Set(),
  previewUplift, onSetPreview, onClose, onChanged,
}) {
  const { toast } = useToast();

  // ── Add-form state ───────────────────────────────────────────────
  const [scope, setScope] = React.useState('global');
  const [clientId, setClientId] = React.useState('');
  const [upliftType, setUpliftType] = React.useState('percent');  // 'percent' | 'fixed_gbp'
  const [percent, setPercent] = React.useState('');
  const [fixedAmount, setFixedAmount] = React.useState('');
  const [fromDate, setFromDate] = React.useState(dateRange?.start || '');
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  // Budget UAT — excluded site IDs for the add-form.
  const [excludedSiteIds, setExcludedSiteIds] = React.useState(() => new Set());

  // ── Inline-edit state (per row) ──────────────────────────────────
  const [editingId, setEditingId] = React.useState(null);
  const [editUpliftType, setEditUpliftType] = React.useState('percent');
  const [editPct, setEditPct] = React.useState('');
  const [editFixedAmount, setEditFixedAmount] = React.useState('');
  const [editFrom, setEditFrom] = React.useState('');
  const [editNotes, setEditNotes] = React.useState('');
  // Budget UAT — excluded sites for the currently-edited row.
  const [editExclSiteIds, setEditExclSiteIds] = React.useState(() => new Set());

  // ── Searchable client combobox (add form) ────────────────────────
  const [clientPickerOpen, setClientPickerOpen] = React.useState(false);
  const selectedClientName = React.useMemo(
    () => clients.find(c => String(c.id) === String(clientId))?.name || '',
    [clients, clientId]
  );

  // Budget UAT — sites belonging to the add-form's selected client group.
  const sitesForClient = React.useMemo(
    () => (clientId
      ? sites.filter(s => String(s.client_id) === String(clientId))
      : []),
    [sites, clientId]
  );

  // Budget UAT — sites belonging to the currently-edited uplift's client group.
  const editingUplift = React.useMemo(
    () => uplifts.find(u => u.id === editingId),
    [uplifts, editingId]
  );
  const sitesForEditingUplift = React.useMemo(
    () => (editingUplift?.scope === 'client' && editingUplift?.client_id
      ? sites.filter(s => String(s.client_id) === String(editingUplift.client_id))
      : []),
    [sites, editingUplift]
  );

  // ── Helpers ──────────────────────────────────────────────────────
  const reset = () => {
    setScope('global'); setClientId('');
    setUpliftType('percent'); setPercent(''); setFixedAmount('');
    setFromDate(dateRange?.start || ''); setNotes('');
    setExcludedSiteIds(new Set());
  };

  const validDate = (d) => {
    if (!d) return false;
    if (dateRange?.start && d < dateRange.start) return false;
    if (dateRange?.end && d > dateRange.end) return false;
    return true;
  };

  // ── Create uplift ────────────────────────────────────────────────
  const save = async () => {
    const isFixed = upliftType === 'fixed_gbp';
    const pct = isFixed ? 0 : parseFloat(percent);
    const famt = isFixed ? parseFloat(fixedAmount) : null;
    if (!isFixed && Number.isNaN(pct)) {
      toast({ title: 'Percent required', description: 'Enter a numeric % (e.g. 5 for +5%).', variant: 'destructive' });
      return;
    }
    if (isFixed && (Number.isNaN(famt) || famt <= 0)) {
      toast({ title: 'Amount required', description: 'Enter a positive £ amount per job.', variant: 'destructive' });
      return;
    }
    if (scope === 'client' && !clientId) {
      toast({ title: 'Client required', description: 'Pick a client for a client-scope uplift.', variant: 'destructive' });
      return;
    }
    if (!validDate(fromDate)) {
      toast({ title: 'Invalid date', description: `Effective date must be within ${dateRange?.start} → ${dateRange?.end}.`, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/reports/revenue-budget/uplifts', {
        scope,
        client_id: scope === 'client' ? parseInt(clientId, 10) : null,
        region_name: null,
        percent: pct,
        from_date: fromDate || null,
        priority: 100,
        notes: notes || null,
        excluded_site_ids: scope === 'client' ? [...excludedSiteIds] : [],
        uplift_type: upliftType,
        fixed_amount: famt,
      });
      const label = isFixed ? `+£${famt}/job` : `+${pct}%`;
      toast({ title: 'Uplift added', description: `${scope} ${label} from ${fromDate}` });
      reset();
      await onChanged();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete uplift ────────────────────────────────────────────────
  const remove = async (id) => {
    if (!window.confirm('Remove this uplift?')) return;
    try {
      const res = await api.delete(`/reports/revenue-budget/uplifts/${id}`);
      const totals = res?.data?.totals || {};
      const n = totals.sites_count || 0;
      const delta = Number(totals.master_delta_total || 0);
      let description;
      if (n === 0) {
        description = 'No published sites needed reverting';
      } else {
        const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
        const magnitude = Math.abs(delta).toFixed(2);
        description = `Reverted ${n} ${n === 1 ? 'site' : 'sites'}: master ${sign}£${magnitude} total`;
      }
      toast({ title: 'Uplift removed', description });
      await onChanged();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  // ── Inline edit ──────────────────────────────────────────────────
  const startEdit = (u) => {
    setEditingId(u.id);
    setEditUpliftType(u.uplift_type || 'percent');
    setEditPct(String(u.percent ?? ''));
    setEditFixedAmount(String(u.fixed_amount ?? ''));
    setEditFrom(u.from_date || (dateRange?.start || ''));
    setEditNotes(u.notes || '');
    setEditExclSiteIds(new Set(u.excluded_site_ids || []));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditExclSiteIds(new Set());
  };
  const saveEdit = async () => {
    const isFixed = editUpliftType === 'fixed_gbp';
    const pct = isFixed ? 0 : parseFloat(editPct);
    const famt = isFixed ? parseFloat(editFixedAmount) : null;
    if (!isFixed && Number.isNaN(pct)) {
      toast({ title: 'Percent required', variant: 'destructive' });
      return;
    }
    if (isFixed && (Number.isNaN(famt) || famt <= 0)) {
      toast({ title: 'Amount required', description: 'Enter a positive £ amount.', variant: 'destructive' });
      return;
    }
    if (!validDate(editFrom)) {
      toast({ title: 'Invalid date', description: `Must be within ${dateRange?.start} → ${dateRange?.end}.`, variant: 'destructive' });
      return;
    }
    const payload = {
      percent: pct, from_date: editFrom, notes: editNotes || null,
      uplift_type: editUpliftType,
      fixed_amount: isFixed ? famt : null,
    };
    if (editingUplift?.scope === 'client') {
      payload.excluded_site_ids = [...editExclSiteIds];
    }
    try {
      await api.patch(`/reports/revenue-budget/uplifts/${editingId}`, payload);
      toast({ title: 'Uplift updated' });
      setEditingId(null);
      setEditExclSiteIds(new Set());
      await onChanged();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  // ── Scope label (incl. exclusion count) ──────────────────────────
  const scopeLabel = (u) => {
    if (u.scope === 'global') return 'All sites';
    if (u.scope === 'client') {
      const c = clients.find(x => x.id === u.client_id);
      const base = `Client · ${c ? c.name : `#${u.client_id}`}`;
      const exclCount = u.excluded_site_ids?.length ?? 0;
      return exclCount > 0 ? `${base} (${exclCount} excl.)` : base;
    }
    if (u.scope === 'region') return `Region · ${u.region_name} (legacy)`;
    return u.scope;
  };

  // Format uplift value for display in the list
  const upliftValueLabel = (u) => {
    if (u.uplift_type === 'fixed_gbp') {
      return `+£${Number(u.fixed_amount || 0).toFixed(2)}/job`;
    }
    const sign = (u.percent || 0) >= 0 ? '+' : '';
    return `${sign}${u.percent ?? 0}%`;
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="uplift-dialog" className="max-w-[95vw] w-[95vw]">
        <DialogHeader>
          <DialogTitle>Manage Uplifts</DialogTitle>
        </DialogHeader>

        <div className="text-[11px] rounded bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1.5">
          Uplifted £/job values are <b>rounded UP to the nearest £</b> in the projection, exports, and write-back to schedule.
        </div>

        {/* ── Add uplift form ──────────────────────────────────── */}
        <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
          <div className="text-sm font-semibold text-slate-700">Add uplift</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Scope</Label>
              <Select value={scope} onValueChange={(v) => {
                setScope(v);
                // Clear exclusions when switching away from client scope.
                if (v !== 'client') setExcludedSiteIds(new Set());
              }}>
                <SelectTrigger data-testid="uplift-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global (all sites)</SelectItem>
                  <SelectItem value="client">Client group</SelectItem>
                  <SelectItem value="region" disabled>Region (use header toggle)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[10px] mt-1 leading-snug">
                {scope === 'global' ? (
                  <span className="text-slate-500">
                    Applied to <b>all clients that do not have a client-specific uplift</b>. Client uplifts override this — they do <b>not</b> compound.
                  </span>
                ) : scope === 'client' ? (
                  <span className="text-slate-500">
                    Client uplift <b>replaces</b> any global uplift for this client — no compounding.
                  </span>
                ) : null}
              </div>
            </div>

            {scope === 'client' && (
              <div>
                <Label className="text-xs">Client</Label>
                <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={clientPickerOpen}
                      className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      data-testid="uplift-client"
                    >
                      <span className={selectedClientName ? '' : 'text-slate-400'}>
                        {selectedClientName || 'Type to search clients…'}
                      </span>
                      <span className="ml-2 text-slate-400">▾</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search clients…" data-testid="uplift-client-search" />
                      <CommandList className="max-h-72">
                        <CommandEmpty>No matches</CommandEmpty>
                        <CommandGroup>
                          {clients.map(c => (
                            <CommandItem
                              key={c.id}
                              value={c.name}
                              onSelect={() => {
                                // Clear exclusions when client changes — different sites.
                                setExcludedSiteIds(new Set());
                                setClientId(String(c.id));
                                setClientPickerOpen(false);
                              }}
                              data-testid={`uplift-client-option-${c.id}`}
                            >
                              {c.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div>
              <Label className="text-xs">Uplift type &amp; amount</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex rounded-md border border-input overflow-hidden text-xs">
                  <button
                    type="button"
                    className={`px-3 py-1.5 font-medium transition-colors ${upliftType === 'percent' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    onClick={() => setUpliftType('percent')}
                    data-testid="uplift-type-percent"
                  >% uplift</button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 font-medium transition-colors ${upliftType === 'fixed_gbp' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    onClick={() => setUpliftType('fixed_gbp')}
                    data-testid="uplift-type-fixed"
                  >£ fixed</button>
                </div>
                {upliftType === 'percent' ? (
                  <Input type="number" step="0.1" placeholder="e.g. 5 or -2.5"
                    value={percent} onChange={(e) => setPercent(e.target.value)}
                    data-testid="uplift-percent" className="flex-1" />
                ) : (
                  <Input type="number" step="0.01" min="0.01" placeholder="e.g. 12.50"
                    value={fixedAmount} onChange={(e) => setFixedAmount(e.target.value)}
                    data-testid="uplift-fixed-amount" className="flex-1" />
                )}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {upliftType === 'percent'
                  ? 'Percentage applied to the per-job price; result rounded UP to nearest £'
                  : 'Fixed £ added to each job price; result rounded UP to nearest £'}
              </div>
              {/* Applies-to preview counter */}
              {(() => {
                let count = 0;
                if (scope === 'global') {
                  const coveredByClient = Array.from(clientsWithClientUpliftIds).reduce((acc, cid) => acc + (siteCountByClient[cid] || 0), 0);
                  count = Math.max(0, totalSiteCount - coveredByClient);
                } else if (scope === 'client' && clientId) {
                  count = Math.max(0, (siteCountByClient[Number(clientId)] || 0) - excludedSiteIds.size);
                }
                return (
                  <div className="text-[10px] text-emerald-700 mt-0.5" data-testid="uplift-applies-to-count">
                    Will apply to <b>{count}</b> site{count === 1 ? '' : 's'}
                    {scope === 'global' && clientsWithClientUpliftIds.size > 0 && (
                      <span className="text-slate-500"> · {clientsWithClientUpliftIds.size} client{clientsWithClientUpliftIds.size === 1 ? '' : 's'} excluded (already client-uplifted)</span>
                    )}
                    {scope === 'client' && excludedSiteIds.size > 0 && (
                      <span className="text-amber-700 font-medium"> · {excludedSiteIds.size} site{excludedSiteIds.size === 1 ? '' : 's'} excluded</span>
                    )}
                  </div>
                );
              })()}
            </div>

            <div>
              <Label className="text-xs">Effective from</Label>
              <Input type="date" value={fromDate}
                min={dateRange?.start} max={dateRange?.end}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="uplift-from-date" />
              <div className="text-[10px] text-slate-500 mt-1">Must be within {dateRange?.start} → {dateRange?.end}</div>
            </div>

            <div className="md:col-span-2">
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Annual RPI 2027"
                data-testid="uplift-notes" />
            </div>

            {/* Budget UAT — Site exclusion multi-select: visible only for client-scope
                uplifts where a client has been chosen. */}
            {scope === 'client' && clientId && (
              <div className="md:col-span-2">
                <Label className="text-xs">
                  Exclude sites from uplift
                  <span className="ml-1 text-slate-400 font-normal">(optional — sites below will keep their current price)</span>
                </Label>
                <SiteExclusionPicker
                  sites={sitesForClient}
                  selected={excludedSiteIds}
                  onChange={setExcludedSiteIds}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="uplift-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add uplift
            </Button>
          </div>
        </div>

        {/* ── Active uplifts table ─────────────────────────────── */}
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Active uplifts ({uplifts.length})</div>
          {uplifts.length === 0 ? (
            <div className="text-xs text-slate-500 italic py-4 text-center border rounded">
              No uplifts configured. Uplifts are applied multiplicatively on top of £/job for projected audits.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Scope</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">From</th>
                    <th className="text-left px-3 py-2">Notes</th>
                    <th className="text-left px-3 py-2">Excl. Sites</th>
                    <th className="px-3 py-2 w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {uplifts.map(u => {
                    const isEditing = editingId === u.id;
                    const isFixed = (u.uplift_type === 'fixed_gbp') || (isEditing && editUpliftType === 'fixed_gbp');
                    const sitesForRow = u.scope === 'client' && u.client_id
                      ? sites.filter(s => String(s.client_id) === String(u.client_id))
                      : [];
                    return (
                      <tr key={u.id} className="border-t" data-testid={`uplift-row-${u.id}`}>
                        <td className="px-3 py-2">{scopeLabel(u)}</td>
                        {isEditing ? (
                          <>
                            <td className="px-3 py-2">
                              <div className="flex gap-1 items-center">
                                <div className="flex rounded border border-input overflow-hidden text-[10px]">
                                  <button type="button"
                                    className={`px-2 py-1 ${editUpliftType === 'percent' ? 'bg-slate-700 text-white' : 'bg-white text-slate-600'}`}
                                    onClick={() => setEditUpliftType('percent')}>%</button>
                                  <button type="button"
                                    className={`px-2 py-1 ${editUpliftType === 'fixed_gbp' ? 'bg-slate-700 text-white' : 'bg-white text-slate-600'}`}
                                    onClick={() => setEditUpliftType('fixed_gbp')}>£</button>
                                </div>
                                {editUpliftType === 'percent' ? (
                                  <Input type="number" step="0.1" className="h-7 w-16 text-right"
                                    value={editPct} onChange={(e) => setEditPct(e.target.value)}
                                    data-testid={`edit-uplift-percent-${u.id}`} />
                                ) : (
                                  <Input type="number" step="0.01" min="0.01" className="h-7 w-16 text-right"
                                    value={editFixedAmount} onChange={(e) => setEditFixedAmount(e.target.value)}
                                    data-testid={`edit-uplift-fixed-${u.id}`} />
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <Input type="date" className="h-7"
                                min={dateRange?.start} max={dateRange?.end}
                                value={editFrom} onChange={(e) => setEditFrom(e.target.value)}
                                data-testid={`edit-uplift-from-${u.id}`} />
                            </td>
                            <td className="px-3 py-2">
                              <Input className="h-7"
                                value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                                data-testid={`edit-uplift-notes-${u.id}`} />
                            </td>
                            {/* Budget UAT — inline exclusion picker for client-scope rows */}
                            <td className="px-3 py-2 min-w-[200px]">
                              {u.scope === 'client' ? (
                                <SiteExclusionPicker
                                  sites={sitesForRow}
                                  selected={editExclSiteIds}
                                  onChange={setEditExclSiteIds}
                                />
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button type="button" onClick={saveEdit}
                                className="p-1 rounded hover:bg-emerald-100 text-emerald-700 mr-1"
                                title="Save"
                                data-testid={`save-uplift-${u.id}`}>
                                <Check className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" onClick={cancelEdit}
                                className="p-1 rounded hover:bg-slate-100 text-slate-500"
                                title="Cancel">
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2 text-right font-mono">
                              <span className={
                                u.uplift_type === 'fixed_gbp'
                                  ? 'text-blue-700'
                                  : (u.percent >= 0 ? 'text-emerald-700' : 'text-rose-700')
                              }>
                                {upliftValueLabel(u)}
                              </span>
                            </td>
                            <td className="px-3 py-2">{u.from_date || <span className="text-slate-400">—</span>}</td>
                            <td className="px-3 py-2 text-slate-600">{u.notes || <span className="text-slate-400">—</span>}</td>
                            {/* Budget UAT — display exclusion count */}
                            <td className="px-3 py-2">
                              {u.scope === 'client' ? (
                                u.excluded_site_ids?.length > 0 ? (
                                  <span
                                    className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 font-medium"
                                    title={`Excluded site IDs: ${u.excluded_site_ids.join(', ')}`}
                                    data-testid={`uplift-excl-count-${u.id}`}
                                  >
                                    {u.excluded_site_ids.length} excl.
                                  </span>
                                ) : (
                                  <span className="text-slate-400 text-xs" data-testid={`uplift-excl-count-${u.id}`}>—</span>
                                )
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {/* Preview toggle */}
                              <button type="button"
                                onClick={() => {
                                  const isPreviewing = previewUplift?._sourceId === u.id;
                                  if (isPreviewing) { onSetPreview && onSetPreview(null); return; }
                                  onSetPreview && onSetPreview({
                                    _sourceId: u.id,
                                    scope: u.scope,
                                    client_id: u.client_id,
                                    excludeClientIds: clientsWithClientUpliftIds,
                                  });
                                }}
                                className={
                                  'p-1 rounded mr-1 ' +
                                  (previewUplift?._sourceId === u.id
                                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                    : 'hover:bg-emerald-100 text-emerald-700')
                                }
                                title={previewUplift?._sourceId === u.id ? 'Stop preview' : 'Preview affected sites in grid'}
                                data-testid={`preview-uplift-${u.id}`}>
                                <TrendingUp className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" onClick={() => startEdit(u)}
                                className="p-1 rounded hover:bg-slate-100 text-slate-500 mr-1"
                                title="Edit"
                                data-testid={`edit-uplift-${u.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" onClick={() => remove(u.id)}
                                className="p-1 rounded hover:bg-rose-100 text-rose-600"
                                title="Remove"
                                data-testid={`delete-uplift-${u.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
